import type { SimpleSite } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  Form,
  MaterialProductAutocomplete,
  TextField,
  TradeCompanySiteSelector,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { useState } from 'react';
import { Button, Stack } from 'ui';

import { useCreateSiteMaterial } from '../../hooks/useCreateSiteMaterial/useCreateSiteMaterial';

const schema = yup.object().shape({
  material_product: yup.number().required(),
  material_spec: yup.number().optional().nullable(),
  seller_label: yup.string().optional(),
  site: yup.number().required(),
});

type Props = {
  companyId: number;
};

export type FormValues = yup.InferType<typeof schema>;

export const NewCompanySiteMaterialForm = ({ companyId }: Props) => {
  const [siteMaterial, setSiteMaterial] = useState<SimpleSite | null>(null);
  const form = useForm({
    resolver: yupResolver(schema),
  });
  const { mutate: createSiteMaterial, isPending } = useCreateSiteMaterial(form);

  const materialSpec = form.watch('material_spec');

  const onSubmit = (data: FormValues) => {
    createSiteMaterial({
      data: {
        material_product_id: data.material_product,
        seller_label: data.seller_label,
        trade_site_id: data.site,
      },
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit} data-testid="">
      <TradeCompanySiteSelector
        name="site"
        label="Site"
        tradeCompanyId={companyId}
        onSelectOption={(site) => setSiteMaterial(site)}
        required
      />
      <AllowedMaterialSpecAutocomplete name="material_spec" label="Material spec" />
      <Stack direction="column" alignItems="flex-end" gap={1}>
        <Button
          variant="text"
          component="a"
          disabled={!siteMaterial}
          target="_blank"
          href={`/ground-control/material-products/create`}
          aria-label="Create material product"
        >
          Create material product
        </Button>
        <MaterialProductAutocomplete
          name="material_product"
          label="Material Product"
          country={siteMaterial?.country}
          material_spec_id={materialSpec || undefined}
          required
          disabled={!siteMaterial}
        />
      </Stack>
      <TextField
        name="seller_label"
        label="Seller Label"
        infoTooltip="Seller Label (the grade the seller calls this product, e.g. 95/5)"
      />
      <Button fullWidth type="submit" loading={isPending}>
        Create
      </Button>
    </Form>
  );
};
